import * as React from 'react'
import { FormikHelpers } from 'formik'
import styled from 'styled-components'

import { Forms, Form, Password, useFormsContext } from '@ally/forms'
import {
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  Label,
  TextBody,
} from '@ally/metronome-ui'
import { AllyUserRole, AsyncStatus } from '@ally/transmitigator'

import { useTransmit } from '../../TransmitContext'
import { InputWrapper } from '../Shared'
import { getDisplayRole } from '../../Utils'

const FORM_ID = 'enterprise-login-form'

const StyledCheckbox = styled(Checkbox)`
  margin-top: 10px;
`

interface EnterpriseLoginFormProps {
  enterpriseUsername: string
  userRoles: AllyUserRole
  status: AsyncStatus
}

interface EnterpriseLoginFormState {
  username: string
  password: string
  rememberMeFlag: boolean
}

const EnterpriseLoginFormContent: React.FC<EnterpriseLoginFormProps> = ({
  enterpriseUsername,
  userRoles,
  status,
}) => {
  const { setFieldValue, values } = useFormsContext()

  const handleSaveUsernameChange = (
    event: React.FormEvent<HTMLInputElement>,
  ): void => {
    const isChecked = event.currentTarget.checked

    setFieldValue('rememberMeFlag', isChecked)
  }

  return (
    <Form id={FORM_ID}>
      <InputWrapper mb="md">
        <Label htmlFor="">Ally {getDisplayRole(userRoles)} username</Label>
        <TextBody size="sm" tag="p">
          {enterpriseUsername}
        </TextBody>
      </InputWrapper>

      <InputWrapper mb="md">
        <Password adornment="passwordMask" name="password" useDefaultValidate />
      </InputWrapper>

      <ButtonGroup>
        <Button
          allytmln="Continue"
          loading={status === 'Loading'}
          text="Continue"
          type="submit"
          variant="primary"
        />
        <Box>
          <StyledCheckbox
            label="Save username"
            name="rememberMeFlag"
            isChecked={values.rememberMeFlag}
            onChange={handleSaveUsernameChange}
          />
        </Box>
      </ButtonGroup>
    </Form>
  )
}

export const EnterpriseLoginForm: React.FC<EnterpriseLoginFormProps> = ({
  enterpriseUsername,
  userRoles,
  status,
}) => {
  const { actions, navigateToForgotPassword } = useTransmit()
  const initialValues = {
    username: enterpriseUsername,
    password: '',
    rememberMeFlag: false,
  }

  const handleOnSubmit = (
    formValues: EnterpriseLoginFormState,
    { setSubmitting }: FormikHelpers<EnterpriseLoginFormState>,
  ): void => {
    actions.loginWithUser(formValues)

    setSubmitting(false)
  }

  const handleForgotPasswordClick = (): void => {
    navigateToForgotPassword({ username: enterpriseUsername })
  }

  return (
    <>
      <Forms
        formId={FORM_ID}
        initialValues={initialValues}
        onSubmit={handleOnSubmit}
      >
        <EnterpriseLoginFormContent
          enterpriseUsername={enterpriseUsername}
          userRoles={userRoles}
          status={status}
        />
      </Forms>
      <Box mt="md">
        Forgot{' '}
        <Button
          allytmln="forgotPassword"
          onClick={handleForgotPasswordClick}
          text="password"
          variant="link"
        />
        ?
      </Box>
    </>
  )
}
